import { useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import {
  AiJobContext,
  type AiJobContextValue,
  type AiJobSnapshot,
  type AiJobSource,
  type BeginJobArgs,
  type BeginJobResult,
} from './aiJobContextCore'
import { publishAmbientSignal } from '../services/ai/ambient/ambientSignalBus'
import { ToastContext } from './toastContextCore'
import { WorkspaceContext } from './workspaceContextCore'
import { getNlsString } from '../nls/nlsRuntime'
import { requestWorkspaceSwitch } from '../services/workspaceSwitchService'
import { resolveAiLanding } from '../services/workspaceSwitch/switchLanding'

interface AiJobProviderProps {
  children: ReactNode
  currentWorkspaceId?: string
}

interface RunningJobInternals {
  controller: AbortController
  source: AiJobSource
  workspaceId: string | null
}

interface StashedSlot {
  source: AiJobSource
  result: unknown
  workspaceId: string | null
}

/**
 * Hosts the single in-flight AI job. Lives near the top of the React tree so
 * a job survives unmount of its originating panel — that's what powers the
 * "close modal, come back, click Review changes" flow.
 */
export const AiJobProvider = ({ children, currentWorkspaceId }: AiJobProviderProps) => {
  const workspaceContext = useContext(WorkspaceContext)
  const toastContext = useContext(ToastContext)

  const effectiveWorkspaceId = currentWorkspaceId ?? workspaceContext?.activeWorkspaceId ?? null
  const effectiveWorkspaceIdRef = useRef<string | null>(effectiveWorkspaceId)
  useEffect(() => {
    effectiveWorkspaceIdRef.current = effectiveWorkspaceId
  }, [effectiveWorkspaceId])

  const [snapshot, setSnapshot] = useState<AiJobSnapshot>({
    status: 'idle',
    source: null,
    label: null,
    workspaceId: null,
  })

  // Refs avoid stale closures across the async beginJob lifecycle and let
  // subscribers register without re-rendering.
  const runningRef = useRef<RunningJobInternals | null>(null)
  const stashedRef = useRef<StashedSlot | null>(null)
  const liveHandlersRef = useRef<Partial<Record<AiJobSource, (result: unknown) => void>>>({})

  const reset = useCallback(() => {
    runningRef.current = null
    setSnapshot({ status: 'idle', source: null, label: null, workspaceId: null })
  }, [])

  const beginJob = useCallback(<T,>({ source, label, run, workspaceId }: BeginJobArgs<T>): BeginJobResult<T> => {
    if (runningRef.current) {
      return {
        outcome: Promise.reject(new Error('An AI job is already running. Abort it before starting another.')),
      }
    }

    const jobWorkspaceId = workspaceId ?? effectiveWorkspaceIdRef.current ?? null
    const controller = new AbortController()
    runningRef.current = { controller, source, workspaceId: jobWorkspaceId }
    // Clear any previously stashed result before starting a new run.
    stashedRef.current = null

    setSnapshot({ status: 'running', source, label: label ?? null, workspaceId: jobWorkspaceId })

    const outcome = (async (): Promise<T | null> => {
      try {
        const result = await run(controller.signal)
        if (controller.signal.aborted) return null

        // Publish an ambient signal so downstream copilot heuristics can
        // react (e.g. AC-DRAFT after ≥3 new subtasks under one parent).
        // Payload is best-effort: the evaluator tolerates missing fields.
        try {
          const createdIds = extractCreatedIds(result)
          publishAmbientSignal({
            kind: 'AI_JOB_COMPLETED',
            workspaceId: extractWorkspaceId(result) ?? jobWorkspaceId ?? '',
            payload: { source, createdIds },
          })
        } catch (publishError) {
          console.warn('[ambient] publish AI_JOB_COMPLETED failed', publishError)
        }

        const currentActive = effectiveWorkspaceIdRef.current
        const isSameWorkspace = !jobWorkspaceId || !currentActive || currentActive === jobWorkspaceId
        const handler = liveHandlersRef.current[source]

        if (isSameWorkspace && handler) {
          // Consumer is mounted in the same workspace — hand off immediately and clear.
          reset()
          handler(result)
        } else {
          // Consumer was gone or in another workspace when the job finished — stash for later claim.
          stashedRef.current = { source, result, workspaceId: jobWorkspaceId }
          runningRef.current = null
          setSnapshot({ status: 'ready', source, label: null, workspaceId: jobWorkspaceId })

          if (!isSameWorkspace && jobWorkspaceId) {
            const targetWs = workspaceContext?.workspaces?.find((w) => w.id === jobWorkspaceId)
            const targetName = targetWs?.name || targetWs?.slug || jobWorkspaceId
            const toastMsg = getNlsString('NLSWorkspaceSwitchAiJobFinished', { workspaceName: targetName })
            const actionLabel = getNlsString('NLSWorkspaceSwitchAiJobOpenAction') || 'Open'
            const targetLanding = resolveAiLanding(targetWs)

            toastContext?.showToast(toastMsg, 'info', {
              label: actionLabel,
              run: () => {
                void requestWorkspaceSwitch({
                  toWorkspaceId: jobWorkspaceId,
                  landing: targetLanding,
                  source: 'USER',
                })
              },
            })
          }
        }
        return result
      } catch (error) {
        if (controller.signal.aborted) return null
        reset()
        throw error
      }
    })()

    return { outcome }
  }, [reset, toastContext, workspaceContext])

  const abort = useCallback(() => {
    const running = runningRef.current
    if (!running) return
    running.controller.abort()
    setSnapshot({ status: 'aborted', source: running.source, label: null, workspaceId: running.workspaceId })
    // Clear immediately — the consumer can observe 'aborted' transiently via
    // the snapshot returned synchronously above if it needs to. Move to idle
    // on the next tick so subscribers can resync.
    runningRef.current = null
    queueMicrotask(() => {
      // Only step down if no new job started in the interim.
      if (!runningRef.current) {
        stashedRef.current = null
        setSnapshot({ status: 'idle', source: null, label: null, workspaceId: null })
      }
    })
  }, [])

  const claim = useCallback(<T = unknown,>(source: AiJobSource, workspaceId?: string): T | null => {
    const slot = stashedRef.current
    if (!slot || slot.source !== source) return null
    const checkWsId = workspaceId ?? effectiveWorkspaceIdRef.current
    if (slot.workspaceId && checkWsId && slot.workspaceId !== checkWsId) return null
    stashedRef.current = null
    setSnapshot({ status: 'idle', source: null, label: null, workspaceId: null })
    return slot.result as T
  }, [])

  const clear = useCallback(() => {
    stashedRef.current = null
    runningRef.current = null
    setSnapshot({ status: 'idle', source: null, label: null, workspaceId: null })
  }, [])

  const subscribeAutoHandoff = useCallback(<T,>(source: AiJobSource, handler: (result: T) => void) => {
    const wrapped = (result: unknown) => { (handler as (val: unknown) => void)(result) }
    liveHandlersRef.current[source] = wrapped
    return () => {
      if (liveHandlersRef.current[source] === wrapped) {
        delete liveHandlersRef.current[source]
      }
    }
  }, [])

  const value = useMemo<AiJobContextValue>(
    () => ({ snapshot, beginJob, abort, claim, clear, subscribeAutoHandoff }),
    [snapshot, beginJob, abort, claim, clear, subscribeAutoHandoff],
  )

  return <AiJobContext.Provider value={value}>{children}</AiJobContext.Provider>
}

/**
 * Best-effort extraction of newly-created node IDs from an arbitrary job
 * result — accepts common shapes: {createdIds: string[]}, {goal:{id,tasks:[{id,subTasks:[{id}]}]}}. Returns [] when nothing matches so the ambient
 * signal is still fired (heuristics tolerate an empty list).
 */
const extractCreatedIds = (result: unknown): string[] => {
  if (!result || typeof result !== 'object') return []
  const bag = result as Record<string, unknown>
  if (Array.isArray(bag.createdIds)) {
    return bag.createdIds.filter((entry): entry is string => typeof entry === 'string')
  }
  const goal = bag.goal as { id?: unknown; tasks?: Array<{ id?: unknown; subTasks?: Array<{ id?: unknown }> }> } | undefined
  if (!goal) return []
  const ids: string[] = []
  if (typeof goal.id === 'string') ids.push(goal.id)
  goal.tasks?.forEach((task) => {
    if (typeof task.id === 'string') ids.push(task.id)
    task.subTasks?.forEach((sub) => { if (typeof sub.id === 'string') ids.push(sub.id) })
  })
  return ids
}

const extractWorkspaceId = (result: unknown): string | null => {
  if (!result || typeof result !== 'object') return null
  const bag = result as Record<string, unknown>
  const wid = bag.workspaceId
  return typeof wid === 'string' ? wid : null
}
